import { useCallback, useEffect, useMemo, useState } from 'react';
import useGlobals from './useGlobals';
import { Group, User } from '../Types';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { userService } from '../Services/userService';
import { groupService } from '../Services/groupService';

const useUser = () => {
    const { handleNavigate, userSettings, i18n, demo } = useGlobals();
    let { userIdentifier } = useParams();
    const [user, setUser] = useState<User>();
    const [maxCosts, setMaxCosts] = useState<number>(0.0);
    const [userSnapshot, setUserSnapshot] = useState<User | null>(null);
    const [userLoaded, setUserLoaded] = useState<boolean>(false);
    const [groups, setGroups] = useState<Group[]>([]);
    const [groupsLoaded, setGroupsLoaded] = useState<boolean>(false);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'user.toast.success', sourceName: 'Users' }));
    }, [i18n]);

    const handleSave = useCallback(() => {
        if (demo) {
            notifySuccess();
            return;
        }
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;

        const data = new URLSearchParams();
        data.append('maxCosts', maxCosts.toString());

        userService.saveUser(userIdentifier, data).then((response) => {
            setUserSnapshot(response.data);
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [demo, maxCosts, userIdentifier, userSettings.isLoggedIn, notifySuccess]);

    const handleMaxCostChange = useCallback((value: number) => {
        setMaxCosts(value);
    }, []);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;

        userService.getUser(userIdentifier).then((response) => {
            setUser(response.data);
            setUserSnapshot(response.data);
            setMaxCosts(parseFloat(parseFloat(response.data.maxCosts).toFixed(2)));
            setUserLoaded(true);
        });
    }, [userSettings.isLoggedIn, userIdentifier, demo]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;

        groupService.getGroupsByUser(userIdentifier).then((response) => {
            setGroups(response.data);
            setGroupsLoaded(true);
        });
    }, [userSettings.isLoggedIn, userIdentifier, demo]);

    const UserActions = useMemo(
        () => ({
            handleSave,
            handleMaxCostChange,
        }),
        [handleSave, handleMaxCostChange],
    );

    return {
        UserActions,
        handleNavigate,
        userSnapshot,
        userLoaded,
        i18n,
        maxCosts,
        user,
        groups,
        groupsLoaded,
    };
};

export default useUser;
